import { useEffect, useState, useMemo, useContext } from "react"

import { Trans, useTranslation } from "gatsby-plugin-react-i18next"
import gsap from "gsap"
import styled, { css } from "styled-components"

import Waves from "components/Banners/Wave"
import InnerWrapper from "components/InnerWrapper"
import { ScreenContext } from "components/Providers"
import { ReactComponent as CurvedLineSVG } from "images/homepage/curvedLine.svg"
import { ReactComponent as CurvedLineMSVG } from "images/homepage/curvedLineM.svg"
import colors from "styles/colors"
import text from "styles/text"
import { fmobile, fresponsive, ftablet } from "utils/fullyResponsive"
import useMedia from "utils/useMedia"

const topStopColors = [colors.darkMode, colors.darkMode, colors.darkMode]
const bottomStopColors = [colors.blue700, colors.blue700, colors.blue700]

export default function Logos({
  allLogos,
}: {
  allLogos: { one: string; two: string }[]
}) {
  const [inner, setInner] = useState<HTMLDivElement | null>(null)
  const { mobile } = useContext(ScreenContext)
  const { t } = useTranslation()

  const alIntlLogos = useMemo(
    () =>
      allLogos.map((logo, i) => {
        return (
          <LogoWrapper key={`logo-${logo.one}`}>
            <Logo
              className={i === 0 ? "logo-first logo" : "logo"}
              src={logo.one}
              loading="lazy"
              alt={t("company logo") ?? "company logo"}
            />
            <Logo1
              className="logo1"
              src={logo.two}
              alt={t("company logo") ?? "company logo"}
              loading="lazy"
            />
          </LogoWrapper>
        )
      }),
    [t, allLogos]
  )
  useEffect(() => {
    if (inner) {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: inner,
          start: "top bottom",
          end: "bottom top",
          toggleActions: "play pause resume pause",
        },
        repeat: -1,
        repeatDelay: 0,
      })

      tl.to(".logo", { stagger: 0.5, y: "-=100%", duration: 0.4 }, 0)
        .to(".logo1", { stagger: 0.5, y: "-=100%", duration: 0.4 }, 0)
        .set(".logo", { stagger: 0.5, y: "+=200%" }, 0.5)
        .to(".logo1", { stagger: 0.5, y: "-=100%", duration: 0.4 }, 2.5)
        .to(".logo", { stagger: 0.5, y: "-=100%", duration: 0.4 }, 2.5)
        .set(".logo1", { stagger: 0.5, y: "+=200%" }, 5)

      return () => {
        tl.kill()
      }
    }
  }, [inner])

  return (
    <Wrapper ref={ref => setInner(ref)}>
      <Waves
        top
        bottomStopColors={bottomStopColors}
        topStopColors={topStopColors}
        waveHeight={useMedia("", "", "", "20vw")}
      />
      <InnerWrapper center column>
        <Content>
          <Title>
            <Trans>
              The world&apos;s most innovative companies choose Galileo.
            </Trans>
          </Title>
          {!mobile ? <CurvedLine /> : <CurvedLineM />}
          {!mobile ? <CurvedLine2 /> : <CurvedLine2M />}
          <AllLogos>{alIntlLogos}</AllLogos>
        </Content>
      </InnerWrapper>
    </Wrapper>
  )
}

const LogoWrapper = styled.div`
  overflow: hidden;
  display: flex;
  flex-direction: column;
  position: relative;

  ${fresponsive(css`
    width: 150px;
    height: 40px;
  `)}

  ${fmobile(css`
    margin-bottom: 20px;
  `)}
`

const Logo = styled.img`
  ${fresponsive(css`
    width: 150px;
    height: 40px;
  `)}
`

const Logo1 = styled.img`
  position: absolute;
  top: 100%;

  ${fresponsive(css`
    width: 150px;
    height: 40px;
  `)}
`

const Wrapper = styled.div`
  width: 100%;
  position: relative;
  z-index: 2;
  display: flex;
  justify-content: center;
  background: ${colors.darkMode};

  ${fresponsive(css`
    height: 252px;
  `)}

  ${ftablet(css`
    height: 150px;
    margin-bottom: -2px;
  `)}

  ${fmobile(css`
    height: 394px;
  `)}
`

const Content = styled.div`
  text-align: center;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;

  ${fresponsive(css`
    margin-top: -192px;
  `)}

  ${ftablet(css`
    margin-top: -47px;
  `)}

  ${fmobile(css`
    margin-top: 38px;
  `)}
`

const Title = styled.h5`
  ${text.h5};
  color: ${colors.blue50};

  ${fresponsive(css`
    margin-bottom: 70px;
  `)}

  ${ftablet(css`
    ${text.bodyL};
  `)}

  ${fmobile(css`
    ${text.bodyR};
    text-align: center;
    width: 233px;
    margin-bottom: 40px;
  `)}
`

const CurvedLine = styled(CurvedLineSVG)`
  position: absolute;

  ${fresponsive(css`
    top: 24px;
    width: 280px;
    height: 129px;
    left: 0;
  `)}

  ${ftablet(css`
    width: 129px;
    height: 127px;
    left: -26px;
    top: -18px;
  `)}
`

const CurvedLineM = styled(CurvedLineMSVG)`
  position: absolute;

  left: 0;

  ${fmobile(css`
    top: 24px;
    transform: rotate(-180deg);
    width: 50px;
    height: 50px;
  `)}
`

const CurvedLine2 = styled(CurvedLine)`
  left: auto;
  right: 0;
  transform: rotate(180deg);

  ${fresponsive(css`
    top: -103px;
  `)}

  ${ftablet(css`
    top: -74px;
    right: -26px;
  `)}
`

const CurvedLine2M = styled(CurvedLineM)`
  transform: rotate(0);
  right: 0;
  left: auto;

  ${fmobile(css`
    top: -19px;
  `)}
`

const AllLogos = styled.div`
  display: flex;
  justify-content: space-between;

  ${fresponsive(css`
    width: 1143px;
  `)}

  ${ftablet(css`
    width: 834px;
  `)}

  ${fmobile(css`
    flex-wrap: wrap;
    width: 100%;
    justify-content: center;
  `)}
`
